import Alert from '@mui/material/Alert';
import { optionalFieldLabel } from '@openx/components-form/optionalFieldLabel.ts';
import { type DealFormData, dealFormField } from '@openx/types/deal.ts';
import type { HierarchyItemWithLevels } from '@openx/types/hierarchyItem.ts';
import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import { HierarchyAutoComplete } from '../HierarchyAutoComplete/HierarchyAutoComplete.tsx';
import { NoOptionsInfo } from './components/NoOptionsInfo.tsx';

export const BrandAutoComplete = () => {
  const { t } = useTranslation();
  const { useBrandsOptionsFetch } = useDealContext();
  const brands = useBrandsOptionsFetch();
  const { setValue } = useFormContext<DealFormData>();

  const observableBrandSalesforceID =
    useWatch<Pick<DealFormData, 'brand_salesforce_id'>>({
      name: dealFormField.BRAND_SALESFORCE_ID,
    }) || '';

  const onBrandSelect = useCallback(
    (e: HierarchyItemWithLevels | null): void => {
      setValue(dealFormField.BRAND_SALESFORCE_ID, e?.id ?? null, { shouldDirty: true });
    },
    [setValue]
  );

  const noOptionsText = useCallback(() => <NoOptionsInfo />, []);

  if (brands.loading) {
    return (
      <HierarchyAutoComplete
        data={[]}
        dataMap={{}}
        isLoading={brands.loading}
        onOptionSelect={onBrandSelect}
        value={''}
        placeholderText={t('Search a Brand')}
        noOptionsText={noOptionsText}
        inputLabel={optionalFieldLabel(t('Brand'))}
        testId="brand-autocomplete"
      />
    );
  }

  return (
    <>
      {observableBrandSalesforceID && brands.mapById[observableBrandSalesforceID] === undefined && (
        <Alert severity="warning" sx={theme => ({ marginTop: theme.spacing(1) })}>
          {t('The brand has been removed from Salesforce')}
        </Alert>
      )}

      <HierarchyAutoComplete
        data={brands.list}
        dataMap={brands.mapById}
        isLoading={brands.loading}
        onOptionSelect={onBrandSelect}
        value={observableBrandSalesforceID}
        placeholderText={t('Search a Brand')}
        noOptionsText={noOptionsText}
        inputLabel={optionalFieldLabel(t('Brand'))}
        testId="brand-autocomplete"
      />
    </>
  );
};
